import { motion } from "framer-motion";
import { Star, Globe, ShieldCheck } from "lucide-react";
import { fadeInUp, staggerContainer } from "../data/constants";

export const AboutSection = () => {
  const timeline = [
    {
      year: "2018",
      title: "Nacimiento de HASE Soluciones",
      description: "Nuestra empresa nace en Morelia, Michoacán, con la finalidad de otorgar soluciones tecnológicas confiables.",
    },
    {
      year: "2022",
      title: "Evolución a Flowing",
      description: "Evolucionamos a la marca Flowing como compañía de tecnología e innovación, expandiendo nuestra oferta de servicios.",
    },
    {
      year: "Hoy",
      title: "Líderes en la región",
      description: "Somos el proveedor de internet por fibra óptica de confianza en Tacámbaro, con planes de expansión continuos.",
    },
  ];

  return (
    <section id="nosotros" className="py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="text-orange-500 font-semibold uppercase tracking-wider"
          >
            Sobre Nosotros
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-bold text-gray-900 mt-4"
          >
            Nuestra Historia
          </motion.h2>
        </motion.div>

        {/* Timeline */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {timeline.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative"
            >
              <div className="bg-gray-50 rounded-3xl p-8 h-full">
                <div className="text-5xl font-bold text-orange-500 mb-4">{item.year}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
              {index < timeline.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-orange-300" />
              )}
            </motion.div>
          ))}
        </div>

        {/* Mission, Vision, Values */}
        <div className="grid md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-8 text-white"
          >
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <Star className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Nuestra Misión</h3>
            <p className="text-white/90">
              Ser una empresa comprometida con la calidad y la fluidez en nuestra operación 
              y servicios, ofreciendo soluciones de valor que generen respaldo, seguridad 
              y crecimiento para nuestros clientes.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900 rounded-3xl p-8 text-white"
          >
            <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center mb-6">
              <Globe className="w-7 h-7 text-orange-500" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Nuestra Visión</h3>
            <p className="text-gray-300">
              Proporcionar mayor accesibilidad de red para más personas y empresas 
              de la región, mediante la ampliación de nuestra cobertura de conexión 
              y posicionamiento de nuestros servicios.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gray-50 rounded-3xl p-8"
          >
            <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
              <ShieldCheck className="w-7 h-7 text-orange-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Nuestros Valores</h3>
            <div className="flex flex-wrap gap-2">
              {["Ética", "Servicio", "Compromiso", "Honestidad", "Trabajo en equipo", "Pasión", "Transparencia", "Respeto"].map(
                (value) => (
                  <span
                    key={value}
                    className="px-3 py-1 bg-white rounded-full text-sm text-gray-700 border border-gray-200"
                  >
                    {value}
                  </span>
                )
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
